/**
 * @fileoverview Doctor Dashboard React Query Hook
 * @description Custom hook for fetching doctor dashboard KPIs with caching and error handling
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - React Query with proper caching
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getDoctorKPIs } from '../../api/endpoints/doctor';
import type { DoctorDashboardKPIs, ApiResponse } from '../../types/doctor';

/**
 * React Query key for doctor dashboard KPIs
 */
export const DOCTOR_DASHBOARD_KEYS = {
  all: ['doctor-dashboard'] as const,
  kpis: () => [...DOCTOR_DASHBOARD_KEYS.all, 'kpis'] as const,
} as const;

/**
 * Hook for fetching doctor dashboard KPIs
 * @returns React Query result with KPIs data, loading state, and error handling
 * 
 * @description
 * Features:
 * - Automatic caching with 5-minute stale time
 * - Background refetch every 5 minutes for fresh data
 * - Error retry with exponential backoff
 * - Optimized for dashboard performance
 * 
 * @example
 * ```tsx
 * function DoctorDashboard() {
 *   const { data, isLoading, error, refetch } = useDoctorDashboard();
 * 
 *   if (isLoading) return <LoadingScreen />;
 *   if (error) return <ErrorDisplay error={error} />;
 *   if (!data?.success) return <ErrorDisplay error={data?.error} />;
 * 
 *   const kpis = data.data;
 *   return <DoctorKPICards kpis={kpis} />;
 * }
 * ```
 */
export function useDoctorDashboard(): UseQueryResult<ApiResponse<DoctorDashboardKPIs>, Error> {
  return useQuery({
    queryKey: DOCTOR_DASHBOARD_KEYS.kpis(),
    queryFn: getDoctorKPIs,
    
    // Cache configuration per @ui-ux-excellence.mdc
    staleTime: 5 * 60 * 1000, // 5 minutes - KPIs don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for quick navigation
    
    // Refetch configuration for real-time updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 5 * 60 * 1000, // Background refetch every 5 minutes
    
    // Error handling
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    
    // Performance optimization
    networkMode: 'online', // Only fetch when online
  });
}

/**
 * Extract KPI data safely from hook result
 * @param result - React Query result from useDoctorDashboard
 * @returns KPIs data or null if not available
 * 
 * @example
 * ```tsx
 * const dashboardQuery = useDoctorDashboard();
 * const kpis = extractDashboardKPIs(dashboardQuery);
 * 
 * if (kpis) {
 *   return <div>Upcoming Sessions: {kpis.upcomingSessions}</div>;
 * }
 * ```
 */
export function extractDashboardKPIs(
  result: UseQueryResult<ApiResponse<DoctorDashboardKPIs>, Error>
): DoctorDashboardKPIs | null {
  if (result.data?.success && result.data.data) {
    return result.data.data;
  }
  return null;
}

/**
 * Get error message from dashboard query result
 * @param result - React Query result from useDoctorDashboard
 * @returns User-friendly error message or null
 */
export function getDashboardError(
  result: UseQueryResult<ApiResponse<DoctorDashboardKPIs>, Error>
): string | null {
  if (result.error) {
    return result.error.message || 'Failed to load dashboard data';
  }
  
  if (result.data && !result.data.success && result.data.error) {
    return result.data.error.message || 'Failed to load dashboard data';
  }
  
  return null;
}

/**
 * Check if dashboard has data
 * @param result - React Query result from useDoctorDashboard
 * @returns True if KPIs data is available
 */
export function hasDashboardData(
  result: UseQueryResult<ApiResponse<DoctorDashboardKPIs>, Error>
): boolean {
  return !!extractDashboardKPIs(result);
}

